/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CurrencyEnum } from '@spectrocoin/sc-currencies';
import MerchantPaymentOptions from '../../interfaces/MerchantPaymentOptions';
import MerchantPreOrder from '../../interfaces/MerchantPreOrder';
import { RemoteData, RemoteError, RemoteStatus } from '../SharedTypes';
import { fetchPreorder, fetchPaymentOptions, createOrder } from './PreorderThunks';
import MerchantOrder from '../../interfaces/MerchantOrder';

export enum PreorderStage {
	TravelRule = 1,
	CurrencySelect = 2,
	NetworkSelect = 3,
}

export enum TravelRuleType {
	Personal = 'PRIVATE_PERSON',
	Business = 'COMPANY',
}

export type TravelRuleForm = {
	travelRuleType: TravelRuleType;
	firstName: string;
	lastName: string;
	dateOfBirth: Date | null;
	companyName: string;
	companyCode: string;
	countryOfResidence: string;
	countryOfRegistration: string;
	email: string;
	saveInformationChecked: boolean;
	confirmChecked: boolean;
};

type PreorderState = {
	stage: PreorderStage;
	preorder: RemoteData<MerchantPreOrder>;
	paymentOptions: RemoteData<MerchantPaymentOptions[]>;
	travelRuleForm: TravelRuleForm;
	form: {
		payCurrency: CurrencyEnum | null;
		network: string | null;
	};
	create: RemoteData<MerchantOrder>;
};

const initialState: PreorderState = {
	stage: PreorderStage.TravelRule,
	travelRuleForm: {
		travelRuleType: TravelRuleType.Personal,
		firstName: '',
		lastName: '',
		dateOfBirth: null,
		companyName: '',
		companyCode: '',
		countryOfResidence: '',
		countryOfRegistration: '',
		email: '',
		saveInformationChecked: false,
		confirmChecked: false,
	},
	form: {
		payCurrency: null,
		network: null,
	},
	preorder: {
		status: RemoteStatus.None,
	},
	paymentOptions: {
		status: RemoteStatus.None,
	},
	create: {
		status: RemoteStatus.None,
	},
};

const slice = createSlice({
	initialState,
	name: 'preorder',
	reducers: {
		reset: () => initialState,
		setPayCurrency: (state, { payload }: PayloadAction<CurrencyEnum>) => {
			state.form.payCurrency = payload;
		},
		setNetwork: (state, { payload }: PayloadAction<string | null>) => {
			state.form.network = payload;
		},
		setPreorder: (state, { payload }: PayloadAction<MerchantPreOrder>) => {
			state.preorder.status = RemoteStatus.Done;
			state.preorder.data = payload;
		},
		setStage: (state, { payload }: PayloadAction<PreorderStage>) => {
			state.stage = payload;
		},
		setTravelRuleType: (state, { payload }: PayloadAction<TravelRuleType>) => {
			state.travelRuleForm.travelRuleType = payload;
		},
		setFirstName: (state, { payload }: PayloadAction<string>) => {
			state.travelRuleForm.firstName = payload;
		},
		setLastName: (state, { payload }: PayloadAction<string>) => {
			state.travelRuleForm.lastName = payload;
		},
		setDateOfBirth: (state, { payload }: PayloadAction<Date | null>) => {
			state.travelRuleForm.dateOfBirth = payload;
		},
		setCompanyName: (state, { payload }: PayloadAction<string>) => {
			state.travelRuleForm.companyName = payload;
		},
		setCompanyCode: (state, { payload }: PayloadAction<string>) => {
			state.travelRuleForm.companyCode = payload;
		},
		setCountryOfResidence: (state, { payload }: PayloadAction<string>) => {
			state.travelRuleForm.countryOfResidence = payload;
		},
		setCountryOfRegistration: (state, { payload }: PayloadAction<string>) => {
			state.travelRuleForm.countryOfRegistration = payload;
		},
		setEmail: (state, { payload }: PayloadAction<string>) => {
			state.travelRuleForm.email = payload;
		},
		setSaveInformationChecked: (state, { payload }: PayloadAction<boolean>) => {
			state.travelRuleForm.saveInformationChecked = payload;
		},
		setConfirmChecked: (state, { payload }: PayloadAction<boolean>) => {
			state.travelRuleForm.confirmChecked = payload;
		},
	},
	extraReducers: (builder) =>
		builder
			.addCase(fetchPreorder.pending, (state) => {
				state.preorder.status = RemoteStatus.InProgress;
				delete state.preorder.error;
			})
			.addCase(fetchPreorder.rejected, (state, { payload }) => {
				state.preorder.status = RemoteStatus.Error;
				state.preorder.error = payload as RemoteError;
			})
			.addCase(fetchPreorder.fulfilled, (state, { payload }) => {
				state.preorder.status = RemoteStatus.Done;
				state.preorder.data = payload;
			})
			.addCase(fetchPaymentOptions.pending, (state) => {
				state.paymentOptions.status = RemoteStatus.InProgress;
				delete state.paymentOptions.error;
			})
			.addCase(fetchPaymentOptions.rejected, (state, { payload }) => {
				state.paymentOptions.status = RemoteStatus.Error;
				state.paymentOptions.error = payload as RemoteError;
			})
			.addCase(fetchPaymentOptions.fulfilled, (state, { payload }) => {
				state.paymentOptions.status = RemoteStatus.Done;
				state.paymentOptions.data = payload;
			})
			.addCase(createOrder.pending, (state) => {
				state.create.status = RemoteStatus.InProgress;
				delete state.create.error;
			})
			.addCase(createOrder.rejected, (state, { payload }) => {
				state.create.status = RemoteStatus.Error;
				state.create.error = payload as RemoteError;
			})
			.addCase(createOrder.fulfilled, (state, { payload }) => {
				state.create.status = RemoteStatus.Done;
				state.create.data = payload;
			}),
});

export default slice;
